import React from 'react';
import { trim } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { STATIC_ROOT } from '../../constants';
import { MOMENTJS_DATE_FORMAT } from '../../constants/moment';
import {
    MODAL_TYPE_LOGGED_OUT,
    MODAL_TYPE_LIMITED_ACCESS
} from '../../services/modal/constants';

import { showModal } from '../../services/modal/actions';

import { toDate } from '../../utils/dateUtils';
import { getInstertitielCouponUrl } from '../../utils';
import { expiresWithinOneMonth } from '../../services/coupon/utils';
import { isLimitedMember } from '../../services/authentication/selector';
import { convertHtmlToReactDomWithParams } from '../../utils/htmlUtils';

import SvgComponent from '../SvgComponent';
import ButtonComponent from '../button/ButtonComponent';
import LinkButtonComponent from '../button/LinkButtonComponent';

import './CouponComponent.scss';

class CouponComponent extends React.Component {
    state = { detailsOpen: false };

    toggleDetails = () => this.setState(prevState => ({ detailsOpen: !prevState.detailsOpen }));

    // non connecté → modale de connexion ; membre limité → modale d'information :
    // dans les deux cas le code n'est jamais révélé et la redirection n'a pas lieu
    guardCouponClick = event => {
        const { isAuthenticated, isLimited, showModal } = this.props;

        if (!isAuthenticated) {
            event.preventDefault();
            showModal(MODAL_TYPE_LOGGED_OUT);
            return false;
        }
        if (isLimited) {
            event.preventDefault();
            showModal(MODAL_TYPE_LIMITED_ACCESS);
            return false;
        }
    };

    createCouponTitle = () => {
        const { coupon, partnerName, i18nCoupon } = this.props;

        if (coupon.title) {
            return coupon.title;
        } else if (coupon.reductionType) {
            let titre = '';
            if (coupon.value) {
                titre = `${i18nCoupon.DISCOUNTOF} ${coupon.valueFormat}`;
            } else {
                titre = `${i18nCoupon.DISCOUNT} ${partnerName}`;
            }
            if (coupon.deliveryType) {
                titre += ` ${i18nCoupon.PLUS_FREEDELIVERY}`;
            } else if (coupon.giftType) {
                titre += ` ${i18nCoupon.PLUS_GIFT}`;
            }
            return titre;
        } else if (coupon.deliveryType) {
            return `${i18nCoupon.FREEDELIVERY} ${partnerName}`;
        } else if (coupon.giftType) {
            return `${i18nCoupon.GIFT} ${partnerName}`;
        } else {
            return `${i18nCoupon.ADVANTAGE} ${partnerName}`;
        }
    };

    render() {
        const { coupon, i18nCoupon, isAuthenticated, isLimited, i18n, partnerId, noPicto } =
            this.props;
        // on fait un trim car il se peut que qu'il est juste un espace dans le code et donc ce n'est pas un code promo mais un bon plan
        const code = trim(coupon.code);
        // membre limité : même rendu que non connecté (code caché, pas de redirection)
        const hasFullAccess = isAuthenticated && !isLimited;
        return (
            <div
                className={`fm-coupon grid has-gutter flex-center-alt flex-start ${
                    hasFullAccess ? 'co' : ''
                }`}
                id={coupon.id}>
                {!noPicto && (
                    <div className="fm-coupon-svg hidden-mobonly">
                        <SvgComponent
                            src={`${STATIC_ROOT}/label.svg`}
                            width="36"
                            height="36"
                            aria-hidden="true"
                            focusable="false"
                        />
                    </div>
                )}
                <div className="fm-coupon-offre">
                    <div className="title bold">{this.createCouponTitle()}</div>
                    <div className="descoffre">
                        {trim(coupon.descriptionBullet) && (
                            <React.Fragment>
                                <button
                                    type="button"
                                    className="bt-details"
                                    aria-expanded={this.state.detailsOpen}
                                    aria-controls={`coupon-details-${coupon.id}`}
                                    onClick={this.toggleDetails}>
                                    {i18n.page.MERCHANTCARD.DETAILS}
                                    <SvgComponent
                                        src={`${STATIC_ROOT}/fleche.svg`}
                                        className={`chevron ${
                                            this.state.detailsOpen ? 'haut' : 'bas'
                                        }`}
                                        width="12"
                                        height="12"
                                        aria-hidden="true"
                                        focusable="false"
                                    />
                                </button>
                                {this.state.detailsOpen && (
                                    <div
                                        id={`coupon-details-${coupon.id}`}
                                        className="details-content">
                                        {convertHtmlToReactDomWithParams(coupon.descriptionBullet)}
                                    </div>
                                )}
                            </React.Fragment>
                        )}
                        {coupon.cumulativeWithCashback && (
                            <div className="cumulative">{i18nCoupon.CUMULATIVE}</div>
                        )}
                    </div>
                </div>
                <div className="one-sixth fm-coupon-bt">
                    {hasFullAccess ? (
                        <LinkButtonComponent
                            color={code ? 'fourth' : 'main'}
                            to={getInstertitielCouponUrl({
                                idPartenaire: partnerId,
                                idCoupon: coupon.id
                            })}
                            text={code ? code : i18n.feature.bonplan.button.TEXT}
                            title={code ? code : i18n.feature.bonplan.button.TEXT}
                            target="_blank"
                        />
                    ) : (
                        <ButtonComponent
                            color="main"
                            text={
                                code
                                    ? i18n.feature.coupon.button.TEXT
                                    : i18n.feature.bonplan.button.TEXT
                            }
                            type="button"
                            onClick={this.guardCouponClick}
                        />
                    )}
                    {expiresWithinOneMonth(coupon.endDate) && (
                        <div className="duree">
                            {i18n.page.MERCHANTCARD.VALID}
                            <strong>{toDate(coupon.endDate, MOMENTJS_DATE_FORMAT)}</strong>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
CouponComponent.propTypes = {
    coupon: PropTypes.object.isRequired,
    i18n: PropTypes.object.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    partnerId: PropTypes.string.isRequired,
    partnerName: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    noPicto: PropTypes.bool
};

const mapDispatchToProps = {
    showModal
};

const mapStateToProps = state => {
    return {
        i18nCoupon: state.i18n.page.MERCHANTCARD.COUPON_TITLE,
        isLimited: isLimitedMember(state)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CouponComponent);



// WEBPACK FOOTER //
// ./src/components/coupon/CouponComponent.js